﻿import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LayoutDashboard, Gift, Sparkles, Users, Settings, LogOut,
  ChevronLeft, ChevronRight, ExternalLink,
  Shield, LifeBuoy, Share2, Box,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useWedding } from '../../context/WeddingContext';
import { cn } from '../../lib/utils';
import { coupleNamePair, getCoupleCollectiveTermOf } from '../../lib/coupleName';

const NAV_ITEMS = [
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/dashboard/cards', label: 'Meus Presentes', icon: Gift },
  { path: '/dashboard/ai-generator', label: 'IA M\u00e1gica', icon: Sparkles },
  { path: '/dashboard/rsvps', label: 'Lista de Presen\u00e7as', icon: Users },
  { path: '/dashboard/mimos3d', label: 'Mimos em 3D', icon: Box },
  { path: '/dashboard/indicacoes', label: 'Indique e ganhe', icon: Share2 },
  { path: '/dashboard/settings', label: 'Configura\u00e7\u00f5es', icon: Settings },
  { path: '/dashboard/support', label: 'Suporte', icon: LifeBuoy },
];

export function Sidebar({ onClose }) {
  const [collapsed, setCollapsed] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { wedding } = useWedding();
  const collectiveTermOf = getCoupleCollectiveTermOf(wedding);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const coupleName = wedding?.groomName && wedding?.brideName
    ? coupleNamePair(wedding)
    : user?.name || 'Meu Casamento';
  const isAdmin = user?.email === 'admin@mimodocasal.com.br';

  const recipientStatus = (() => {
    const hasWallet = Boolean(wedding?.asaas?.walletId);
    const general = String(wedding?.asaas?.statusCache?.general || '').toUpperCase();
    if (!hasWallet) return { label: 'Recebedor não configurado', dot: 'bg-gray-400', wrap: 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100' };
    if (general === 'APPROVED') return { label: 'Recebedor ativo', dot: 'bg-emerald-500', wrap: 'bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100' };
    return { label: 'Recebedor pendente', dot: 'bg-amber-500 animate-pulse', wrap: 'bg-amber-50 border-amber-200 text-amber-800 hover:bg-amber-100' };
  })();

  return (
    <motion.aside
      animate={{ width: collapsed ? 80 : 260 }}
      transition={{ duration: 0.3, ease: 'easeInOut' }}
      className="relative flex flex-col h-full bg-white border-r border-gray-100 shadow-soft overflow-hidden flex-shrink-0"
    >
      {/* Logo */}
      <div className="flex items-center gap-3 px-5 py-5 border-b border-gray-100">
        <img src="/brand/icon.png" alt="Mimo do Casal" className="flex-shrink-0 w-9 h-9 object-contain rounded-xl shadow-romantic" />
        <AnimatePresence>
          {!collapsed && (
            <motion.div
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -10 }}
              transition={{ duration: 0.2 }}
            >
              <span className="font-bold text-gray-900 text-lg tracking-tight">Mimo do Casal</span>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Couple name + recipient status */}
      <AnimatePresence>
        {!collapsed && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="px-5 py-4 border-b border-gray-100 space-y-3"
          >
            <div>
              <p className="text-xs text-gray-400 mb-0.5">Casamento de</p>
              <p className="text-sm font-semibold text-gray-800 truncate">{coupleName}</p>
            </div>
            <Link
              to="/dashboard/settings?tab=payment"
              onClick={onClose}
              className={cn('flex items-center gap-2 px-2.5 py-1.5 rounded-lg border text-[11px] font-semibold transition-colors', recipientStatus.wrap)}
              title="Ver status do recebedor"
            >
              <span className={cn('w-2 h-2 rounded-full flex-shrink-0', recipientStatus.dot)} />
              <span className="truncate">{recipientStatus.label}</span>
            </Link>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Nav */}
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto no-scrollbar">
        {NAV_ITEMS.map(({ path, label, icon: Icon }) => {
          const active = location.pathname === path;
          return (
            <Link key={path} to={path} onClick={onClose}>
              <motion.div
                whileHover={{ x: collapsed ? 0 : 2 }}
                className={cn(
                  'flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 group cursor-pointer',
                  active
                    ? 'bg-rose-50 text-rose-600 shadow-sm'
                    : 'text-gray-500 hover:bg-gray-50 hover:text-gray-800',
                  collapsed && 'justify-center px-2'
                )}
              >
                <Icon size={19} className={cn(active ? 'text-rose-500' : 'text-gray-400 group-hover:text-gray-600')} />
                <AnimatePresence>
                  {!collapsed && (
                    <motion.span
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="text-sm font-medium"
                    >
                      {label}
                    </motion.span>
                  )}
                </AnimatePresence>
                {active && !collapsed && (
                  <div className="ml-auto w-1.5 h-1.5 bg-rose-500 rounded-full" />
                )}
              </motion.div>
            </Link>
          );
        })}

        <div className="pt-3 border-t border-gray-100">
          {wedding?.slug && (
            <Link to={`/${wedding.slug}`} target="_blank">
              <div className={cn(
                'flex items-center gap-3 px-3 py-2.5 rounded-xl border border-rose-100 bg-rose-50 text-rose-700 hover:bg-rose-100 transition-all cursor-pointer group shadow-sm',
                collapsed && 'justify-center px-2'
              )}>
                <ExternalLink size={19} className="text-rose-500" />
                <AnimatePresence>
                  {!collapsed && (
                    <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="text-sm font-medium">
                      Ver página {collectiveTermOf}
                    </motion.span>
                  )}
                </AnimatePresence>
              </div>
            </Link>
          )}
          {isAdmin && (
            <Link to="/admin" onClick={onClose}>
              <div className={cn(
                'flex items-center gap-3 px-3 py-2.5 rounded-xl text-gray-500 hover:bg-gray-50 hover:text-gray-800 transition-all cursor-pointer group',
                collapsed && 'justify-center px-2'
              )}>
                <Shield size={19} className="text-gray-400 group-hover:text-gray-600" />
                <AnimatePresence>
                  {!collapsed && (
                    <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="text-sm font-medium">
                      Admin
                    </motion.span>
                  )}
                </AnimatePresence>
              </div>
            </Link>
          )}
        </div>
      </nav>

      {/* User */}
      <div className="px-3 pb-4 border-t border-gray-100 pt-4">
        <button
          onClick={handleLogout}
          className={cn(
            'flex items-center gap-3 w-full px-3 py-2.5 rounded-xl text-gray-500 hover:bg-red-50 hover:text-red-600 transition-all group',
            collapsed && 'justify-center px-2'
          )}
        >
          <LogOut size={19} className="group-hover:text-red-500" />
          <AnimatePresence>
            {!collapsed && (
              <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="text-sm font-medium">
                Sair
              </motion.span>
            )}
          </AnimatePresence>
        </button>
      </div>

      {/* Toggle desktop only */}
      <button
        onClick={() => setCollapsed(!collapsed)}
        className={cn(
          'hidden lg:flex absolute right-2 z-20 w-7 h-7 bg-white border border-gray-200 rounded-full shadow-sm items-center justify-center hover:bg-gray-50 transition-colors',
          collapsed ? 'top-16' : 'top-5'
        )}
      >
        {collapsed ? <ChevronRight size={14} className="text-gray-500" /> : <ChevronLeft size={14} className="text-gray-500" />}
      </button>
    </motion.aside>
  );
}

